import { createFileRoute } from '@tanstack/react-router'
import { useReport } from '@/hooks/use-report'
import { useDeferredValue } from 'react'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { DistributionPending } from './-components/shared/distribution-pending'
import { Distribution } from './-components/shared/distribution'
import { EmptyState } from './-components/shared/empty-state'
import { GroupContextProvider } from './-components/shared/group-context'

export const Route = createFileRoute('/_app/offers/$offerId/distribution')({
  loaderDeps: ({ search }) => ({ type: search.type }),
  context: ({ deps, params }) => ({
    offerApplicantIndicatorsQueryOptions: trpc.companies.getOfferApplicantIndicators.queryOptions({
      offerId: Number(params.offerId),
      type: deps.type,
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.offerApplicantIndicatorsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: DistributionPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
  pendingMs: 0,
  pendingMinMs: 0,
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { report } = useReport(context.reportQueryOptions)
  // Deferred so changing the type filter keeps the PREVIOUS view on screen while the new data loads —
  // the options object comes from the route's context, so this is the very object the loader warmed.
  const deferredApplicants = useDeferredValue(context.offerApplicantIndicatorsQueryOptions)
  const { data: offerApplicants } = useSuspenseQuery(deferredApplicants)

  const members = (offerApplicants?.members ?? []).map((member) => ({
    ...member,
    id: member.id,
  }))

  if (offerApplicants?.emptyStateCode) {
    return <EmptyState code={offerApplicants.emptyStateCode} surface="distribution" />
  }

  return (
    <GroupContextProvider type="offer">
      <Distribution report={report} profiles={members} data={offerApplicants?.distribution} showFilters />
    </GroupContextProvider>
  )
}
